import React, { useEffect, useRef, useState } from 'react';
import { Send, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactEmail = "contato@smartsolutionsgroup.com.br";
const contactPhone = "+55 (65) 99608-7984";

const ContactSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Define o endpoint: usa o script PHP na raiz para produção, ou a API local para desenvolvimento
      const endpoint = process.env.NODE_ENV === 'production'
        ? '/send_email.php'
        : `${API}/quotes`;

      const response = await axios.post(endpoint, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        message: formData.message
      });

      if (response.data.success) {
        toast.success('Solicitação enviada com sucesso!', {
          description: 'Nossa equipe entrará em contato em breve.'
        });

        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        });
      } else {
        toast.error('Erro ao enviar', {
          description: response.data.message || 'Tente novamente mais tarde.'
        });
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Erro ao enviar', {
        description: 'Não foi possível enviar sua solicitação. Tente novamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contato"
      ref={sectionRef}
      className="relative py-24 sm:py-32 bg-black overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] bg-amber-900/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div
          className={`text-center mb-16 sm:mb-20 ${isVisible ? 'is-visible' : ''
            }`}
        >
          <span className="inline-block text-amber-500 text-sm font-medium tracking-widest uppercase mb-4">
            Entre em Contato
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 overflow-hidden">
            <span className="block split-text-inner">Solicitar Cotação</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto overflow-hidden">
            <span className="block split-text-inner">
              Preencha o formulário abaixo e nossa equipe entrará em contato
              com uma proposta personalizada para sua empresa.
            </span>
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          <div
            className={`lg:col-span-2 transition-all duration-1000 ease-out delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
              }`}
          >
            <div className="space-y-8">
              <div className="group flex items-start gap-4 p-4 rounded-xl transition-all duration-300 hover:bg-slate-900/50">
                <div className="w-12 h-12 rounded-lg bg-amber-600/10 border border-amber-600/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Telefone</h3>
                  <p className="text-slate-400">{contactPhone}</p>
                </div>
              </div>

              <div className="group flex items-start gap-4 p-4 rounded-xl transition-all duration-300 hover:bg-slate-900/50">
                <div className="w-12 h-12 rounded-lg bg-amber-600/10 border border-amber-600/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">E-mail</h3>
                  <p className="text-slate-400">{contactEmail}</p>
                </div>
              </div>

              <div className="group flex items-start gap-4 p-4 rounded-xl transition-all duration-300 hover:bg-slate-900/50">
                <div className="w-12 h-12 rounded-lg bg-amber-600/10 border border-amber-600/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Localização</h3>
                  <p className="text-slate-400">Campinas, SP - Brasil</p>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-800/50">
              <p className="text-slate-500 text-sm italic">
                "Atendimento personalizado para empresas que buscam excelência."
              </p>
            </div>
          </div>

          <div
            className={`lg:col-span-3 transition-all duration-1000 ease-out delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
              }`}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Nome Completo *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Seu nome"
                    className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-amber-600/50 focus:ring-amber-600/20 transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    E-mail Corporativo *
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="seu@empresa.com.br"
                    className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-amber-600/50 focus:ring-amber-600/20 transition-all duration-300"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Empresa *
                  </label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    placeholder="Nome da empresa"
                    className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-amber-600/50 focus:ring-amber-600/20 transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Telefone
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(11) 99999-9999"
                    className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-amber-600/50 focus:ring-amber-600/20 transition-all duration-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-300 text-sm font-medium mb-2">
                  Mensagem *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Descreva os produtos ou serviços que você precisa..."
                  className="bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-amber-600/50 focus:ring-amber-600/20 transition-all duration-300 resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto bg-amber-600 hover:bg-amber-500 text-white px-8 py-6 text-base font-medium transition-all duration-500 hover:shadow-xl hover:shadow-amber-500/25 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Enviar Solicitação
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
